import { memo } from "react";
import { Card } from "react-bootstrap";

import {
    NullableNumber,
    ReportEdgeItem,
    ReportItemSummaryType,
    ReportTimeRangeType,
} from "types/Reports";

import { getTitleByTime } from "./utils/reportString";

import ReportOUCPartialDescription from "./ReportOUCPartialDescription";
import ReportLoadingChart from "./ReportLoadingChart";
import ReportEmptyChart from "./ReportEmptyChart";
import ReportBarChart from "./ReportBarChart";
import ReportBulletText from './ReportBulletText';
import ReportTotalText from './ReportTotalText';

type ReportOverallUsageCardProps = {
    title: string;
    timeRangeType: ReportTimeRangeType;
    itemType: ReportItemSummaryType;
    isLoading: boolean;

    textHighlightClassName: string;

    numberOfItems: number;
    data: NullableNumber[];
    categories: string[];

    dataName: string;

    total: NullableNumber;

    percent: NullableNumber;
    highestItem: ReportEdgeItem;
    lowestItem: ReportEdgeItem;
    average: NullableNumber;
    prevAverage: NullableNumber;

    unit: string;

    colorHex: string;
    dateFormat: string;

    selectedMonth: moment.Moment;
};

export default memo(function ReportOverallUsageCard({
    title,
    timeRangeType,
    itemType,
    isLoading,

    textHighlightClassName,

    numberOfItems,
    data,
    categories,

    dataName,

    total,

    percent,
    highestItem,
    lowestItem,
    average,
    prevAverage,

    unit,

    colorHex,
    dateFormat,

    selectedMonth,
}: Readonly<ReportOverallUsageCardProps>) {
    const isValid = data.some((item => item !== null));
    let body = isLoading ? <ReportLoadingChart /> : <ReportEmptyChart />;

    let unitDisplay = unit;

    if (!isValid && itemType === 'room') {
        unitDisplay = 'hrs';
    }

    if (isValid && !isLoading) {
        const description = <ReportOUCPartialDescription
            itemType={itemType}
            inputPercent={percent}
            numberOfItems={numberOfItems}
            selectedMonth={selectedMonth}
            dateFormat={dateFormat}
            highestItem={highestItem}
            lowestItem={lowestItem}
            unit={unitDisplay}
            average={average}
            prevAverage={prevAverage}
        />;

        body = <>
            <ReportBulletText
                itemType={itemType}
                highlightClassName={textHighlightClassName}
                isOverview={true}
            />

            <ReportBarChart
                itemType={itemType}
                x={categories}
                y={data}
                unit={itemType === 'room' ? 'hours' : unit}
                color={colorHex}
                yName={dataName}
            />

            {description}
        </>;
    }

    const titleTimeText = getTitleByTime(timeRangeType);

    return (
        <Card className="ouc">
            <h6 className="ouc-title">{title}{titleTimeText}</h6>
            <ReportTotalText
                isValid={isValid}
                textHighlightClassName={textHighlightClassName}
                itemType={itemType}
                total={total}
                numberOfItems={numberOfItems}
            />

            {body}
        </Card>
    );
});
